<template>
	<div>
		<form-sidebar>
			<v-form method="post" @submit.prevent="submitForm">
				<form-section
					icon="badge"
					eyebrow="Paso 1"
					title="Identificación"
					description="Con estos datos la aplicación encuentra y ordena la acción en la barra."
				>
					<v-layout wrap>
						<v-flex xs12 sm6 pb-3 pr-2>
							<v-text-field
								background-color="white"
								box
								class="body-1"
								hint="Identificador corto que usa el front (Ej.: NP, CONTA)."
								persistent-hint
								label="Código"
								required
								v-model="$v.model.code.$model"
								:error="$v.model.code.$error"
							>
							</v-text-field>
						</v-flex>
						<v-flex xs12 sm6 pb-3>
							<v-text-field
								background-color="white"
								box
								class="body-1"
								hint="Debe coincidir con el nombre de la ruta web."
								persistent-hint
								label="Url Web"
								required
								v-model="$v.model.codeWeb.$model"
								:error="$v.model.codeWeb.$error"
							>
							</v-text-field>
						</v-flex>
						<v-flex xs12 sm6 pb-3 pr-2>
							<v-text-field
								background-color="white"
								box
								class="body-1"
								hide-details
								label="Fragment Móvil"
								type="number"
								v-model.number="model.numFragment"
							>
							</v-text-field>
						</v-flex>
						<v-flex xs12 sm6 pb-3>
							<v-text-field
								background-color="white"
								box
								class="body-1"
								hide-details
								label="Posición"
								type="number"
								v-model="$v.model.position.$model"
								:error="$v.model.position.$error"
							>
							</v-text-field>
						</v-flex>
						<v-flex xs12>
							<v-combobox
								background-color="white"
								box
								small-chips
								class="body-1"
								clearable
								hint="Escribe la combinación y presiona Enter (Ej.: F2, ctrl+k)."
								persistent-hint
								label="Teclas de acceso rápido"
								multiple
								v-model="model.hotKeys"
							>
								<template slot="selection" slot-scope="{ item }">
									<v-chip
										close
										small
										@input="removeKey(item)"
									>
										<strong>{{ item }}</strong>&nbsp;
									</v-chip>
								</template>
							</v-combobox>
						</v-flex>
					</v-layout>
				</form-section>
				<form-section
					icon="translate"
					eyebrow="Paso 2"
					title="Contenido por idioma"
					description="Nombre, descripción y material de apoyo que verá el usuario final."
					:badge="languagesBadge"
				>
					<div
						class="language-block"
						v-for="(language, index) in model.languages"
						:key="language.id"
					>
						<header class="language-block__head">
							<span class="language-block__code">{{ index }}</span>
							<span
								class="language-block__state"
								:class="{ 'language-block__state--ready': languageIsComplete(language) }"
							>
								<v-icon>{{ languageIsComplete(language) ? 'check_circle' : 'error_outline' }}</v-icon>
								{{ languageIsComplete(language) ? 'Completo' : 'Falta nombre o descripción' }}
							</span>
						</header>
						<v-layout wrap>
							<v-flex xs12 pb-3>
								<v-text-field
									background-color="white"
									box
									class="body-1"
									hide-details
									label="Nombre"
									v-model="language.name"
								>
								</v-text-field>
							</v-flex>
							<v-flex xs12 pb-3>
								<v-textarea
									background-color="white"
									box
									class="body-1"
									height="80"
									hide-details
									label="Descripción"
									no-resize
									v-model="language.description"
								>
								</v-textarea>
							</v-flex>
							<v-flex xs12 pb-3>
								<v-text-field
									background-color="white"
									box
									class="body-1"
									hide-details
									label="Link de video"
									v-model="language.link"
								>
								</v-text-field>
							</v-flex>
							<v-flex xs12>
								<div class="language-block__manuals">
									<div class="language-block__manuals-head">
										<div>
											<strong>Manuales</strong>
											<small>Archivos PDF que se ofrecen como ayuda.</small>
										</div>
										<upload-image
											label-ref="up"
											:label-for="`${index}-manual`"
											:input-id="`${index}-manual`"
											button-title="Cargar archivo"
											@send-file="getManual($event, index)"
										/>
									</div>
									<v-text-field
										solo
										hide-details
										clearable
										readonly
										prepend-inner-icon="picture_as_pdf"
										v-for="(manual, indexMan) in language.manuals"
										:key="indexMan"
										class="body-1 mt-2"
										v-model="manual.file"
										@click:clear="updateManuals(index, indexMan)"
									/>
									<p
										v-if="!language.manuals || !language.manuals.length"
										class="language-block__manuals-empty"
									>
										Sin manuales cargados.
									</p>
								</div>
							</v-flex>
						</v-layout>
					</div>
				</form-section>
				<form-section
					icon="image"
					eyebrow="Paso 3"
					title="Ícono"
					description="Se muestra en la barra de acciones, sobre fondo oscuro."
					tone="info"
				>
					<v-layout wrap align-center class="icon-row">
						<div
							class="action-icon-preview"
							:class="{ 'action-icon-preview-error': $v.model.pathIcon.$error }"
							:title="model.pathIcon || 'Sin ícono'"
						>
							<img
								v-if="showIconPreview"
								:src="model.pathIcon"
								alt="Ícono de la acción"
								@error="markIconAsBroken"
							>
							<v-icon v-else>broken_image</v-icon>
						</div>
						<upload-image
							label-ref="up"
							label-for="image"
							input-id="image"
							@send-image="getImage"
						/>
						<v-flex class="icon-path-field">
							<v-text-field
								solo
								class="body-1 mt-0"
								hide-details
								readonly
								required
								label="Icono"
								v-model="$v.model.pathIcon.$model"
								:error="$v.model.pathIcon.$error"
							>
							</v-text-field>
						</v-flex>
					</v-layout>
				</form-section>
				<form-section
					icon="local_offer"
					eyebrow="Opcional"
					title="Etiqueta"
					description="Se mostrará junto al nombre de la acción (Ej.: Nuevo, Beta, etc...)."
					tone="warning"
				>
					<template slot="aside">
						<v-switch
							class="ma-0 pa-0 label-switch"
							color="base"
							hide-details
							:label="flagNewName"
							v-model="model.flagNew"
						/>
					</template>
					<v-layout v-if="model.flagNew" row wrap>
						<v-flex
							xs12
							pb-2
							v-for="(language, lanKey) in languagesByProject"
							:key="lanKey"
						>
							<label :for="`new-title-${lanKey}`">{{ language.name }}</label>
							<v-text-field
								background-color="white"
								box
								class="body-1"
								hide-details
								:id="`new-title-${lanKey}`"
								required
								v-model="model.newTitle[lanKey].title"
							></v-text-field>
						</v-flex>
					</v-layout>
					<p v-else class="label-disabled-hint">
						La acción se mostrará sin etiqueta.
					</p>
				</form-section>
				<section class="modal-filter-footer">
					<modal-sidebar-button
						:is-loading="buttonDisabled"
						:on-run-service="loading"
						:helper-text="footerHelper"
						@accept-button-clicked="submitForm"
						@cancel-button-clicked="closeModal"
					>
					</modal-sidebar-button>
				</section>
			</v-form>
		</form-sidebar>
	</div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import helper from '@/shared/helper';
import lib from '@/lib';
import UploadImage from '@/components/shared/upload-image';

const PAGE_ACTION_LIST = 'page-action';

const { removeItemFromArrayByIndex } = lib;
const { getLocalData } = helper;

async function created() {
	this.languagesByProject = getLocalData('project-languages');
	const languagesKeys = Object.keys(this.languagesByProject);
	this.newTitle = languagesKeys.reduce((a, k) => {
		const newA = { ...a };
		newA[k] = { title: '' };
		return newA;
	}, {});
	if (this.mode === 'edit') {
		const headers = {
			Accept: `${process.env.ACCEPT_SERVICE_V2}`,
		};
		const { data: record } = await this.$http.get(`action-bar/${this.actionId}`,
			{ headers });
		if (record.data) {
			if (record.data.flagNew && !record.data.newTitle) {
				const newTitleKeys = Object.keys(this.newTitle);
				newTitleKeys.forEach((k) => {
					const text = k === 'es' ? 'Nuevo' : 'New';
					const kTitle = { title: text };
					this.$set(this.newTitle, k, kTitle);
				});
				record.data.newTitle = { ...this.newTitle };
			}
			this.model = {
				code: record.data.code,
				codeWeb: record.data.codeWeb,
				flagNew: record.data.flagNew,
				hotKeys: record.data.hotKeys,
				languages: record.data.languages,
				newTitle: record.data.newTitle || { ...this.newTitle },
				numFragment: record.data.numFragment,
				pathIcon: record.data.pathIcon,
				position: record.data.position,
			};
			this.verifyManualsExist();
		}
	} else {
		this.loadLanguages();
		this.model.pathIcon = `https:${this.getImageUrl('icon-action.svg')}`;
		this.model.newTitle = { ...this.newTitle };
	}
	this.model.codeProject = this.codeProject;
}

function verifyManualsExist() {
	Object.keys(this.model.languages).forEach((key) => {
		if (!this.model.languages[key].manuals) {
			this.$set(this.model.languages[key], 'manuals', []);
		}
	});
}

function buttonDisabled() {
	return this.$v.$invalid || this.languagesEmpty || this.loading;
}

function closeModal() {
	this.goTo(PAGE_ACTION_LIST);
}

function getImage({ urlImage }) {
	this.iconBroken = false;
	this.model.pathIcon = urlImage;
}

function markIconAsBroken() {
	this.iconBroken = true;
}

function showIconPreview() {
	return Boolean(this.model.pathIcon) && !this.iconBroken;
}

function watchPathIcon() {
	this.iconBroken = false;
}

function flagNewName() {
	return this.model.flagNew ? 'Sí' : 'No';
}

function languageIsComplete(language) {
	return Boolean(language && language.name && language.description);
}

function languagesBadge() {
	const languages = Object.keys(this.model.languages)
		.map(key => this.model.languages[key]);
	const complete = languages.filter(this.languageIsComplete).length;
	return languages.length ? `${complete}/${languages.length} listos` : '';
}

function footerHelper() {
	if (this.loading) return 'Guardando la acción…';
	if (this.languagesEmpty) return 'Completa nombre y descripción en al menos un idioma.';
	if (this.$v.$invalid) return 'Faltan datos obligatorios: código, url, posición e ícono.';
	return 'Todo listo para guardar.';
}

function languagesEmpty() {
	let disabled = true;
	Object.keys(this.model.languages).forEach((key) => {
		if (this.model.languages[key].name
			&& this.model.languages[key].description) {
			disabled = false;
		}
	});
	return disabled;
}

async function loadLanguages() {
	const headers = {
		Accept: `${process.env.ACCEPT_SERVICE_V2}`,
	};
	try {
		const { data: project } = await this.$http.get(`projects/${this.codeProject}`, { headers });
		const projectLanguages = project.data.languages;
		const lang = {};
		Object.keys(projectLanguages).forEach((pLangKey) => {
			if (projectLanguages[pLangKey].isSelected) {
				lang[pLangKey] = {
					id: projectLanguages[pLangKey].id,
					name: '',
					description: '',
					link: '',
					manuals: [],
				};
			}
		});
		const modelLanguages = Object.keys(this.model.languages);
		if (modelLanguages.length === 0) {
			this.model.languages = { ...lang };
		}
	} catch (err) {
		this.showError();
	}
}

function mode() {
	return this.actionId ? 'edit' : 'create';
}

function removeKey(item) {
	const index = this.model.hotKeys.indexOf(item);
	if (index > -1) {
		this.model.hotKeys.splice(index, 1);
	}
}

async function submitForm() {
	const method = this.mode === 'create' ? 'post' : 'put';
	const url = this.mode === 'create' ? 'action-bar' : `action-bar/${this.actionId}`;
	const headers = {
		Accept: `${process.env.ACCEPT_SERVICE_V2}`,
	};
	this.loading = true;
	try {
		await this.$http[method](url, this.model, { headers });
		this.goTo(PAGE_ACTION_LIST);
	} finally {
		this.loading = false;
	}
}

function getManual(man, index) {
	this.model.languages[index].manuals = this.model.languages[index].manuals.concat(man);
}

function updateManuals(index, indexMan) {
	setTimeout(() => {
		this.model.languages[index].manuals
			= removeItemFromArrayByIndex(indexMan, this.model.languages[index].manuals);
	}, 500);
}

function validations() {
	const validation = {
		model: {
			code: {
				required,
			},
			codeProject: {
				required,
			},
			codeWeb: {
				required,
			},
			languages: {
				required,
			},
			newTitle: {
				en: {
					title: {
						required: requiredIf(() =>
							Object.keys(this.newTitle).find(k => k === 'en') && this.model.flagNew),
					},
				},
				es: {
					title: {
						required: requiredIf(() =>
							Object.keys(this.newTitle).find(k => k === 'es') && this.model.flagNew),
					},
				},
			},
			pathIcon: {
				required,
			},
			position: {
				required,
			},
		},
	};
	return validation;
}

function data() {
	return {
		iconBroken: false,
		languagesByProject: {},
		loading: false,
		model: {
			code: '',
			codeProject: '',
			codeWeb: '',
			flagNew: false,
			hotKeys: [],
			languages: {},
			newTitle: {},
			numFragment: null,
			pathIcon: '',
			position: null,
		},
		newTitle: {},
	};
}

export default {
	name: 'action-form',
	created,
	components: {
		UploadImage,
	},
	computed: {
		buttonDisabled,
		flagNewName,
		footerHelper,
		languagesBadge,
		languagesEmpty,
		mode,
		showIconPreview,
	},
	data,
	methods: {
		closeModal,
		getImage,
		getManual,
		languageIsComplete,
		loadLanguages,
		markIconAsBroken,
		removeKey,
		submitForm,
		updateManuals,
		verifyManualsExist,
	},
	props: {
		actionId: {
			type: [Number, String],
		},
		codeProject: {
			type: String,
			required: true,
		},
	},
	validations,
	watch: {
		'model.pathIcon': watchPathIcon,
	},
};
</script>

<style lang="scss" scoped>
.language-block {
	background: #f7f9fc;
	border: 1px solid #e7eef5;
	border-radius: 10px;
	margin-bottom: 12px;
	padding: 13px 14px;

	&:last-child { margin-bottom: 0; }
}

.language-block__head {
	align-items: center;
	display: flex;
	gap: 9px;
	justify-content: space-between;
	margin-bottom: 11px;
}

.language-block__code {
	background: #1a4338;
	border-radius: 7px;
	color: #fff;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: .08em;
	padding: 4px 9px;
	text-transform: uppercase;
}

.language-block__state {
	align-items: center;
	color: #a6711a;
	display: flex;
	font-size: 10px;
	font-weight: 700;
	gap: 4px;

	.icon { color: #e19a18; font-size: 14px; }

	&--ready {
		color: #23834a;
		.icon { color: #2eaa64; }
	}
}

.language-block__manuals {
	background: #fff;
	border: 1px dashed #d7e1ea;
	border-radius: 9px;
	padding: 11px 12px;
}

.language-block__manuals-head {
	align-items: center;
	display: flex;
	gap: 10px;
	justify-content: space-between;

	strong { color: #2b435b; display: block; font-size: 12px; }
	small { color: #8495a5; font-size: 10px; }
}

.language-block__manuals-empty {
	color: #a9b6c2;
	font-size: 11px;
	margin: 8px 0 0;
}

.icon-row {
	gap: 10px;
}

.icon-path-field { min-width: 180px; }

.label-switch { flex: 0 0 auto; }

.label-disabled-hint {
	color: #a9b6c2;
	font-size: 11px;
	margin: 0;
}

.action-icon-preview {
	align-items: center;
	background: linear-gradient(135deg, #2b435b 0%, #3f6187 100%);
	border: 2px solid transparent;
	border-radius: 10px;
	box-shadow: 0 3px 8px rgba(43, 67, 91, .22);
	display: inline-flex;
	flex: 0 0 auto;
	height: 48px;
	justify-content: center;
	overflow: hidden;
	width: 48px;

	img {
		height: 30px;
		object-fit: contain;
		width: 30px;
	}

	.icon { color: rgba(255, 255, 255, .55); font-size: 22px; }
}

.action-icon-preview-error {
	border-color: map-get($colors, error);
}
</style>



// WEBPACK FOOTER //
// src/components/actions/action-form.vue